import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Loader2, Eye, EyeOff, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPackages() {
  const { getAuthHeader } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    level: "",
    price: 99,
    duration_days: 60,
    total_sessions: 60,
    session_duration_minutes: 20
  });
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [customLevel, setCustomLevel] = useState("");

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    // Extract unique levels from existing packages
    const levels = [...new Set(packages.map(p => p.level).filter(Boolean))];
    setAvailableLevels(levels);
  }, [packages]);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/admin/packages`, getAuthHeader());
      setPackages(response.data);
    } catch (error) {
      toast.error("Paketler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingPackage) {
        await axios.put(
          `${API}/admin/packages/${editingPackage.id}`,
          formData,
          getAuthHeader()
        );
        toast.success("Paket güncellendi");
      } else {
        await axios.post(`${API}/admin/packages`, formData, getAuthHeader());
        toast.success("Paket oluşturuldu");
      }
      fetchPackages();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description,
      level: pkg.level,
      price: pkg.price,
      duration_days: pkg.duration_days,
      total_sessions: pkg.total_sessions,
      session_duration_minutes: pkg.session_duration_minutes
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu paketi pasifleştirmek istediğinize emin misiniz?")) return;

    try {
      await axios.delete(`${API}/admin/packages/${id}`, getAuthHeader());
      toast.success("Paket pasifleştirildi");
      fetchPackages();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm("DİKKAT: Bu paketi ve içindeki tüm dersleri KALICI olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz!")) return;

    try {
      await axios.delete(`${API}/admin/packages/${id}/permanent`, getAuthHeader());
      toast.success("Paket kalıcı olarak silindi");
      fetchPackages();
    } catch (error) {
      toast.error("Kalıcı silme işlemi başarısız");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Filter packages based on showInactive toggle
  const activePackages = packages.filter(pkg => pkg.is_active);
  const inactivePackages = packages.filter(pkg => !pkg.is_active);
  const displayedPackages = showInactive ? inactivePackages : activePackages;

  const resetForm = () => {
    setEditingPackage(null);
    setFormData({
      name: "",
      description: "",
      level: "A1",
      price: 99,
      duration_days: 60,
      total_sessions: 60,
      session_duration_minutes: 20
    });
  };

  return (
    <AdminLayout title="Ürünler/Paketler">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <p className="text-neutral-400">Tüm eğitim paketlerini buradan yönetebilirsiniz.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowInactive(!showInactive)}
              className={`border-neutral-700 ${showInactive ? 'bg-orange-600/20 text-orange-400 border-orange-600/30' : 'text-neutral-400 hover:text-white'}`}
              data-testid="toggle-inactive-btn"
            >
              {showInactive ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {showInactive ? `Pasif Paketler (${inactivePackages.length})` : `Pasif Paketleri Göster (${inactivePackages.length})`}
            </Button>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700" data-testid="add-package-btn">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Paket
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-neutral-900 border-neutral-800">
              <DialogHeader>
                <DialogTitle className="text-white font-['Nunito']">
                  {editingPackage ? "Paket Düzenle" : "Yeni Paket Oluştur"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-neutral-300">Paket Adı</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Örn: A1 Başlangıç"
                    required
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="package-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-neutral-300">Açıklama</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Paket açıklaması"
                    required
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="package-desc-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-neutral-300">Seviye</Label>
                    <Input
                      type="text"
                      value={formData.level}
                      onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                      placeholder="Örn: A1, Beginner, Level-1"
                      required
                      className="bg-neutral-800 border-neutral-700 text-white"
                      data-testid="package-level-input"
                      list="level-suggestions"
                    />
                    <datalist id="level-suggestions">
                      {availableLevels.map((l) => (
                        <option key={l} value={l} />
                      ))}
                    </datalist>
                    {availableLevels.length > 0 && (
                      <p className="text-xs text-neutral-500">Mevcut: {availableLevels.join(", ")}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-neutral-300">Fiyat (€)</Label>
                    <Input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                      min="0"
                      step="0.01"
                      required
                      className="bg-neutral-800 border-neutral-700 text-white"
                      data-testid="package-price-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-neutral-300">Süre (gün)</Label>
                    <Input
                      type="number"
                      value={formData.duration_days}
                      onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) })}
                      min="1"
                      required
                      className="bg-neutral-800 border-neutral-700 text-white"
                      data-testid="package-duration-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-neutral-300">Toplam Kredi</Label>
                    <Input
                      type="number"
                      value={formData.total_sessions}
                      onChange={(e) => setFormData({ ...formData, total_sessions: parseInt(e.target.value) })}
                      min="1"
                      required
                      className="bg-neutral-800 border-neutral-700 text-white"
                      data-testid="package-sessions-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-neutral-300">Oturum Süresi</Label>
                    <Input
                      type="number"
                      value={formData.session_duration_minutes}
                      onChange={(e) => setFormData({ ...formData, session_duration_minutes: parseInt(e.target.value) })}
                      min="1"
                      required
                      className="bg-neutral-800 border-neutral-700 text-white"
                      data-testid="package-session-duration-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={saving} data-testid="save-package-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingPackage ? "Güncelle" : "Oluştur"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Packages Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          </div>
        ) : displayedPackages.length === 0 ? (
          <div className="text-center py-12 text-neutral-500">
            {showInactive ? "Pasif paket bulunmuyor." : "Aktif paket bulunmuyor."}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedPackages.map((pkg) => (
              <Card key={pkg.id} className={`border-neutral-800 bg-neutral-900 ${!pkg.is_active ? 'border-orange-600/30 bg-neutral-900/50' : ''}`} data-testid={`package-card-${pkg.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className={pkg.is_active ? "bg-red-600/20 text-red-500 border-red-600/30" : "bg-neutral-700 text-neutral-400"}>
                        {pkg.level}
                      </Badge>
                      {!pkg.is_active && <Badge variant="secondary" className="bg-orange-600/20 text-orange-400 border-orange-600/30">Pasif</Badge>}
                    </div>
                    <div className="flex gap-1">
                      {pkg.is_active ? (
                        <>
                          <Button variant="ghost" size="icon" className="hover:bg-neutral-800" onClick={() => handleEdit(pkg)} data-testid={`edit-package-${pkg.id}`}>
                            <Edit className="w-4 h-4 text-neutral-400" />
                          </Button>
                          <Button variant="ghost" size="icon" className="hover:bg-neutral-800" onClick={() => handleDelete(pkg.id)} data-testid={`delete-package-${pkg.id}`}>
                            <Trash2 className="w-4 h-4 text-neutral-400" />
                          </Button>
                        </>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="hover:bg-red-900/50" 
                          onClick={() => handlePermanentDelete(pkg.id)} 
                          data-testid={`permanent-delete-${pkg.id}`}
                          title="Kalıcı Sil"
                        >
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <CardTitle className="text-lg font-bold text-white font-['Nunito'] mt-2">{pkg.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-neutral-400 text-sm mb-4">{pkg.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-500">{pkg.total_sessions} Toplam Kredi • {pkg.duration_days} gün</span>
                    <span className="text-xl font-bold text-red-500">€{pkg.price}</span>
                  </div>
                  {!pkg.is_active && pkg.deactivated_at && (
                    <div className="mt-3 pt-3 border-t border-neutral-800">
                      <p className="text-xs text-orange-400">
                        Pasifleştirilme: {formatDate(pkg.deactivated_at)}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
